import { Fragment, useState } from 'react';
import type { FsnbWorkAbstract } from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import { priceText } from '../../normBaseLabels';
import { CoefNote, Grid, GroupToggle, Muted, TableWrap } from '../styles';

/** Материалы «по проекту»: абстрактный ресурс нормы задаёт класс и расход, а
 *  конкретный ресурс сметчик выбирает из технологической группы. */
export function AbstractTable({
  items,
  groupsLoaded,
  onOpenResource,
}: {
  items: FsnbWorkAbstract[];
  groupsLoaded: boolean;
  onOpenResource?: (source: string, code: string) => void;
}) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  return (
    <TableWrap>
      <Grid>
        <thead>
          <tr>
            <th>Класс</th>
            <th>Материал</th>
            <th>Ед. изм.</th>
            <th className="num">Расход</th>
            <th>Группа</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a, i) => {
            const expanded = open.has(i);
            return (
              <Fragment key={`${a.code}-${i}`}>
                <tr>
                  <td className="code">{a.code}</td>
                  <td>
                    {a.name}
                    <CoefNote>
                      {!groupsLoaded ? (
                        'Состав технологических групп в эту редакцию не загружен.'
                      ) : a.allowed_total === 0 ? (
                        'Состав группы в «Базе ТГ» не приведён — материал по проекту.'
                      ) : (
                        <GroupToggle
                          type="button"
                          aria-expanded={expanded}
                          onClick={() =>
                            setOpen((s) => {
                              const next = new Set(s);
                              if (next.has(i)) next.delete(i);
                              else next.add(i);
                              return next;
                            })
                          }
                        >
                          {expanded ? 'Скрыть' : 'Допустимые ресурсы'}: {fmtNum(a.allowed_total)}
                        </GroupToggle>
                      )}
                    </CoefNote>
                  </td>
                  <td>{a.unit ?? '—'}</td>
                  <td className="num">{a.quantity === null ? 'по проекту' : fmtNum(a.quantity)}</td>
                  <td className="code small">{a.tech_groups.join(', ') || '—'}</td>
                </tr>
                {expanded &&
                  a.allowed.map((r) => (
                    <tr
                      key={`${a.code}-${r.group_code}-${r.resource_code}`}
                      className={r.name && onOpenResource ? 'clickable sub' : 'sub'}
                      onClick={
                        r.name && onOpenResource
                          ? () => onOpenResource('fsbc', r.resource_code)
                          : undefined
                      }
                    >
                      <td className="code small">{r.resource_code}</td>
                      <td>{r.name ?? 'нет в ФСБЦ этой редакции'}</td>
                      <td>{r.unit ?? '—'}</td>
                      <td className="num">{r.cost === null ? '—' : priceText(r.cost, null)}</td>
                      <td className="code small">{r.group_code}</td>
                    </tr>
                  ))}
                {expanded && a.allowed_total > a.allowed.length && (
                  <tr className="sub">
                    <td colSpan={5}>
                      <Muted>
                        Показаны первые {a.allowed.length} из {fmtNum(a.allowed_total)} — остальные
                        в поиске ресурсов по шифру класса {a.code}.
                      </Muted>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Grid>
    </TableWrap>
  );
}
